/**
 * ResultsPhase.js - Tournament Results Phase Component
 * Displays final standings, awards, leaderboards, certificates, and sharing options.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { copyText } from '../../utils/copyText';
import PlayerStatsPanel from '../player-stats/PlayerStatsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icons mapping for awards
const AWARD_ICONS = {
  'ph-trophy': 'ph-bold ph-trophy',
  'ph-medal': 'ph-bold ph-medal',
  'ph-baseball': 'ph-bold ph-baseball',
  'ph-target': 'ph-bold ph-target',
  'ph-hand-grabbing': 'ph-bold ph-hand-grabbing',
  'ph-star': 'ph-bold ph-star',
  'ph-fire': 'ph-bold ph-fire',
  'ph-flame': 'ph-bold ph-flame',
  'ph-arrow-up-right': 'ph-bold ph-arrow-up-right',
  'ph-lightning': 'ph-bold ph-lightning',
  'ph-chart-line-down': 'ph-bold ph-chart-line-down',
  'ph-handshake': 'ph-bold ph-handshake',
};

// Award color palette
const AWARD_COLORS = {
  champion: { bg: 'bg-gradient-to-br from-yellow-400 to-amber-500', text: 'text-white', border: 'border-yellow-300' },
  runner_up: { bg: 'bg-gradient-to-br from-gray-300 to-gray-400', text: 'text-gray-800', border: 'border-gray-200' },
  third_place: { bg: 'bg-gradient-to-br from-amber-600 to-amber-700', text: 'text-white', border: 'border-amber-500' },
  mvp: { bg: 'bg-gradient-to-br from-purple-500 to-indigo-600', text: 'text-white', border: 'border-purple-400' },
  best_batter: { bg: 'bg-gradient-to-br from-blue-500 to-blue-600', text: 'text-white', border: 'border-blue-400' },
  best_bowler: { bg: 'bg-gradient-to-br from-red-500 to-red-600', text: 'text-white', border: 'border-red-400' },
  best_fielder: { bg: 'bg-gradient-to-br from-green-500 to-green-600', text: 'text-white', border: 'border-green-400' },
  most_sixes: { bg: 'bg-gradient-to-br from-orange-500 to-orange-600', text: 'text-white', border: 'border-orange-400' },
  highest_score: { bg: 'bg-gradient-to-br from-pink-500 to-rose-600', text: 'text-white', border: 'border-pink-400' },
  best_bowling_figures: { bg: 'bg-gradient-to-br from-teal-500 to-cyan-600', text: 'text-white', border: 'border-teal-400' },
  best_strike_rate: { bg: 'bg-gradient-to-br from-violet-500 to-purple-600', text: 'text-white', border: 'border-violet-400' },
  best_economy: { bg: 'bg-gradient-to-br from-emerald-500 to-green-600', text: 'text-white', border: 'border-emerald-400' },
  fair_play: { bg: 'bg-gradient-to-br from-sky-500 to-blue-600', text: 'text-white', border: 'border-sky-400' },
};

const DEFAULT_AWARD_COLOR = { bg: 'bg-gradient-to-br from-gray-500 to-gray-600', text: 'text-white', border: 'border-gray-400' };

// ----- SUB-COMPONENTS -----

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#E54562] border-t-transparent"></div>
    </div>
  );
}

function ChampionBanner({ summary }) {
  if (!summary?.champion_team_name) return null;
  
  return (
    <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 rounded-2xl p-6 text-center relative overflow-hidden" data-testid="champion-banner">
      {/* Confetti overlay effect */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjMpIi8+PC9zdmc+')] opacity-50"></div>
      
      <div className="relative z-10">
        <i className="ph-bold ph-trophy text-5xl text-white drop-shadow-lg mb-3 block"></i>
        <p className="text-white/80 text-sm font-medium uppercase tracking-wider mb-1">Tournament Champion</p>
        <h2 className="text-3xl font-black text-white drop-shadow-lg mb-2">{summary.champion_team_name}</h2>
        {summary.runner_up_team_name && (
          <p className="text-white/90 text-sm">
            <i className="ph-bold ph-medal mr-1"></i>
            Runner Up: {summary.runner_up_team_name}
          </p>
        )}
      </div>
    </div>
  );
}

function TournamentStats({ summary }) {
  if (!summary) return null;
  
  const stats = [
    { label: 'Matches', value: summary.total_matches || 0, icon: 'ph-bold ph-calendar-check' },
    { label: 'Runs Scored', value: summary.total_runs?.toLocaleString() || 0, icon: 'ph-bold ph-baseball' },
    { label: 'Wickets', value: summary.total_wickets || 0, icon: 'ph-bold ph-target' },
    { label: 'Boundaries', value: summary.total_boundaries || 0, icon: 'ph-bold ph-arrow-up-right' },
    { label: 'Sixes', value: summary.total_sixes || 0, icon: 'ph-bold ph-arrow-fat-up' },
  ];
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="tournament-stats">
      <h3 className="text-lg font-extrabold mb-4 flex items-center gap-2">
        <i className="ph-bold ph-chart-bar text-[#3AAFA9]"></i>
        Tournament Statistics
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-gray-50 rounded-xl p-4 text-center">
            <i className={`${stat.icon} text-2xl text-[#2B3A67] mb-2 block`}></i>
            <p className="text-2xl font-bold text-[#2B3A67]">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </div>
        ))}
      </div>
      
      {/* Highlights */}
      {(summary.highest_team_score || summary.highest_individual_score) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {summary.highest_team_score && (
            <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
              <i className="ph-bold ph-chart-line-up text-3xl text-blue-500"></i>
              <div>
                <p className="text-sm text-gray-500">Highest Team Score</p>
                <p className="text-xl font-bold text-blue-700">{summary.highest_team_score}</p>
              </div>
            </div>
          )}
          {summary.highest_individual_score && (
            <div className="bg-orange-50 rounded-xl p-4 flex items-center gap-3">
              <i className="ph-bold ph-fire text-3xl text-orange-500"></i>
              <div>
                <p className="text-sm text-gray-500">Highest Individual Score</p>
                <p className="text-xl font-bold text-orange-700">
                  {summary.highest_individual_score}
                  {summary.highest_individual_player_name && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      ({summary.highest_individual_player_name})
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AwardCard({ award }) {
  const colors = AWARD_COLORS[award.award_type] || DEFAULT_AWARD_COLOR;
  const iconClass = AWARD_ICONS[award.icon] || 'ph-bold ph-star';
  
  const winnerName = award.winner_player_name || award.winner_team_name || 'TBD';
  const isTeamAward = award.winner_team_id && !award.winner_player_id;
  
  return (
    <div 
      className={`${colors.bg} ${colors.text} rounded-2xl p-5 shadow-lg transform hover:scale-105 transition-transform duration-200 relative overflow-hidden`}
      data-testid={`award-card-${award.award_type}`}
    >
      {/* Decorative circles */}
      <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full"></div>
      <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white/5 rounded-full"></div>
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <i className={`${iconClass} text-4xl drop-shadow-md`}></i>
          {award.is_finalized && (
            <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
              <i className="ph-bold ph-check-circle mr-1"></i>Official
            </span>
          )}
        </div>
        
        <h4 className="font-bold text-lg mb-1 drop-shadow-sm">{award.title}</h4>
        <p className="text-sm opacity-80 mb-3">{award.description}</p>
        
        <div className="bg-white/20 rounded-xl p-3">
          <p className="text-xs opacity-70 mb-1">{isTeamAward ? 'Team' : 'Player'}</p>
          <p className="font-bold text-lg truncate">{winnerName}</p>
          {award.stat_value && (
            <p className="text-sm font-medium opacity-90 mt-1">{award.stat_value}</p>
          )}
        </div>
        
        {award.prize_amount > 0 && (
          <div className="mt-3 flex items-center gap-1 text-sm">
            <i className="ph-bold ph-currency-inr"></i>
            <span className="font-bold">{award.prize_amount.toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AwardsPanel({ awards, eventId, isAdmin, onRefresh }) {
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem('token');
  
  const teamAwards = awards.filter(a => ['champion', 'runner_up', 'third_place'].includes(a.award_type));
  const playerAwards = awards.filter(a => !['champion', 'runner_up', 'third_place'].includes(a.award_type));
  
  const handleGenerateAwards = async () => {
    if (!window.confirm('This will regenerate all auto-calculated awards. Manually edited awards will be preserved. Continue?')) return;
    
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/awards/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate awards');
      const data = await res.json();
      toast.success(`Generated ${data.count} awards`);
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="awards-panel">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-extrabold flex items-center gap-2">
          <i className="ph-bold ph-medal text-[#F4D341]"></i>
          Tournament Awards
        </h3>
        {isAdmin && (
          <button
            onClick={handleGenerateAwards}
            disabled={generating}
            className="bg-[#2B3A67] hover:bg-[#3d4f87] text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 disabled:opacity-50 transition-colors"
            data-testid="generate-awards-btn"
          >
            {generating ? (
              <><i className="ph-bold ph-spinner animate-spin"></i> Generating...</>
            ) : (
              <><i className="ph-bold ph-magic-wand"></i> Generate Awards</>
            )}
          </button>
        )}
      </div>
      
      {awards.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <i className="ph-bold ph-trophy text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-500 mb-4">No awards have been generated yet.</p>
          {isAdmin && (
            <p className="text-sm text-gray-400">
              Complete matches and generate awards to see winners here.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Team Awards - Full Width */}
          {teamAwards.length > 0 && (
            <div className="mb-6">
              <p className="text-sm text-gray-500 font-medium mb-3">Team Awards</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {teamAwards.map(award => (
                  <AwardCard key={award.id} award={award} />
                ))}
              </div>
            </div>
          )}
          
          {/* Player Awards */}
          {playerAwards.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 font-medium mb-3">Individual Awards</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {playerAwards.map(award => (
                  <AwardCard key={award.id} award={award} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LeaderboardTabs({ eventId }) {
  const [activeTab, setActiveTab] = useState('runs');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const tabs = [
    { key: 'runs', label: 'Most Runs', icon: 'ph-bold ph-baseball' },
    { key: 'wickets', label: 'Most Wickets', icon: 'ph-bold ph-target' },
    { key: 'sixes', label: 'Most Sixes', icon: 'ph-bold ph-arrow-fat-up' },
    { key: 'strike_rate', label: 'Best SR', icon: 'ph-bold ph-lightning' },
    { key: 'economy', label: 'Best Economy', icon: 'ph-bold ph-chart-line-down' },
  ];
  
  useEffect(() => {
    fetchLeaderboard(activeTab);
  }, [activeTab, eventId]);
  
  const fetchLeaderboard = async (category) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/leaderboards/${category}?limit=10`);
      if (!res.ok) throw new Error('Failed to fetch leaderboard');
      const d = await res.json();
      setData(d);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="leaderboards">
      <h3 className="text-lg font-extrabold mb-4 flex items-center gap-2">
        <i className="ph-bold ph-ranking text-[#E54562]"></i>
        Leaderboards
      </h3>
      
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
              activeTab === tab.key
                ? 'bg-[#2B3A67] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`leaderboard-tab-${tab.key}`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#E54562] border-t-transparent"></div>
        </div>
      ) : data?.entries?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 rounded-l-xl">#</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500">Player</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500">Team</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 text-right rounded-r-xl">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.entries.map((entry, idx) => (
                <tr key={entry.player_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    {entry.rank <= 3 ? (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                        entry.rank === 2 ? 'bg-gray-300 text-gray-700' :
                        'bg-amber-600 text-white'
                      }`}>
                        {entry.rank}
                      </span>
                    ) : (
                      <span className="text-gray-500">{entry.rank}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#2B3A67]">{entry.player_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{entry.team_name || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-bold text-[#E54562]">{entry.value}</span>
                    <span className="text-xs text-gray-400 ml-1">
                      ({entry.matches} {entry.matches === 1 ? 'match' : 'matches'})
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400">
          <i className="ph-bold ph-chart-bar text-3xl mb-2 block"></i>
          <p>No data available for this leaderboard yet.</p>
        </div>
      )}
    </div>
  );
}

function CertificatesPanel({ eventId, isAdmin }) {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    fetchCertificates();
  }, [eventId]);
  
  const fetchCertificates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/certificates`);
      if (!res.ok) throw new Error('Failed to fetch certificates');
      const data = await res.json();
      setCertificates(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateCertificates = async (type) => {
    if (!window.confirm(`Generate ${type} certificates for all eligible recipients?`)) return;
    
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/certificates/generate?certificate_type=${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate certificates');
      const data = await res.json();
      toast.success(`Generated ${data.count} certificates`);
      fetchCertificates();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };
  
  const handleDownload = async (certId) => {
    window.open(`${API_URL}/api/v1/events/${eventId}/results/certificates/${certId}/download`, '_blank');
  };
  
  const groupedCerts = {
    award: certificates.filter(c => c.certificate_type === 'award'),
    participation: certificates.filter(c => c.certificate_type === 'participation'),
  };
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="certificates-panel">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-extrabold flex items-center gap-2">
          <i className="ph-bold ph-certificate text-[#3AAFA9]"></i>
          Certificates
        </h3>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => handleGenerateCertificates('award')}
              disabled={generating}
              className="bg-[#F4D341] hover:bg-[#e5c63a] text-[#2B3A67] px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 disabled:opacity-50"
              data-testid="generate-award-certs-btn"
            >
              <i className="ph-bold ph-trophy"></i> Award Certs
            </button>
            <button
              onClick={() => handleGenerateCertificates('participation')}
              disabled={generating}
              className="bg-[#3AAFA9] hover:bg-[#2d9a94] text-white px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1 disabled:opacity-50"
              data-testid="generate-participation-certs-btn"
            >
              <i className="ph-bold ph-users"></i> Participation Certs
            </button>
          </div>
        )}
      </div>
      
      {loading ? (
        <LoadingSpinner />
      ) : certificates.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <i className="ph-bold ph-certificate text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-500">No certificates generated yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Award Certificates */}
          {groupedCerts.award.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 font-medium mb-3">Award Certificates</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {groupedCerts.award.map(cert => (
                  <div key={cert.id} className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <i className="ph-bold ph-trophy text-2xl text-yellow-600"></i>
                      <div>
                        <p className="font-semibold text-sm text-[#2B3A67]">{cert.title}</p>
                        <p className="text-xs text-gray-500">{cert.recipient_name}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(cert.id)}
                      className="p-2 bg-yellow-200 hover:bg-yellow-300 rounded-lg text-yellow-700 transition-colors"
                      title="Download PDF"
                    >
                      <i className="ph-bold ph-download-simple"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Participation Certificates */}
          {groupedCerts.participation.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 font-medium mb-3">Participation Certificates ({groupedCerts.participation.length})</p>
              <div className="bg-gray-50 rounded-xl p-4 max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {groupedCerts.participation.map(cert => (
                    <div key={cert.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg">
                      <div className="flex items-center gap-2">
                        <i className="ph-bold ph-user text-gray-400"></i>
                        <span className="text-sm font-medium">{cert.recipient_name}</span>
                      </div>
                      <button
                        onClick={() => handleDownload(cert.id)}
                        className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600 transition-colors"
                        title="Download PDF"
                      >
                        <i className="ph-bold ph-download-simple text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SharingPanel({ eventId }) {
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    fetchShareData();
  }, [eventId]);
  
  const fetchShareData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/share`);
      if (!res.ok) throw new Error('Failed to fetch share data');
      const data = await res.json();
      setShareData(data);
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleShare = (platform) => {
    if (!shareData?.links?.[platform]) return;
    window.open(shareData.links[platform], '_blank', 'width=600,height=400');
  };
  
  const handleCopy = async () => {
    if (!shareData?.url) return;
    if (await copyText(shareData.url)) {
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Copy failed — select the link and copy it');
    }
  };
  
  if (!shareData) return null;
  
  const socialButtons = [
    { key: 'twitter', label: 'Twitter', icon: 'ph-bold ph-twitter-logo', color: 'bg-[#1DA1F2] hover:bg-[#1a91da]' },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'ph-bold ph-whatsapp-logo', color: 'bg-[#25D366] hover:bg-[#20bd5a]' },
    { key: 'facebook', label: 'Facebook', icon: 'ph-bold ph-facebook-logo', color: 'bg-[#4267B2] hover:bg-[#385899]' },
    { key: 'linkedin', label: 'LinkedIn', icon: 'ph-bold ph-linkedin-logo', color: 'bg-[#0077B5] hover:bg-[#006699]' },
  ];
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid="sharing-panel">
      <h3 className="text-lg font-extrabold mb-4 flex items-center gap-2">
        <i className="ph-bold ph-share-network text-[#E54562]"></i>
        Share Results
      </h3>
      
      <div className="space-y-4">
        {/* Preview Text */}
        <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 whitespace-pre-wrap">
          {shareData.text}
        </div>
        
        {/* Social Buttons */}
        <div className="flex flex-wrap gap-3">
          {socialButtons.map(btn => (
            <button
              key={btn.key}
              onClick={() => handleShare(btn.key)}
              className={`${btn.color} text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors`}
            >
              <i className={btn.icon}></i>
              {btn.label}
            </button>
          ))}
        </div>
        
        {/* Copy Link */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={shareData.url}
            readOnly
            className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm text-gray-600"
          />
          <button
            onClick={handleCopy}
            className={`px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all ${
              copied
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <i className={copied ? 'ph-bold ph-check' : 'ph-bold ph-copy'}></i>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminActionsPanel({ eventId, config, onRefresh }) {
  const [publishing, setPublishing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const token = localStorage.getItem('token');
  
  const handleCalculateSummary = async () => {
    setCalculating(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/summary/calculate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = 'Failed to calculate summary';
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch { /* ignore parse error */ }
        throw new Error(detail);
      }
      toast.success('Summary calculated successfully');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCalculating(false);
    }
  };
  
  const handlePublish = async () => {
    if (!window.confirm('This will finalize all awards and publish the results. This action cannot be undone. Continue?')) return;
    
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/results/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to publish results');
      toast.success('Results published successfully!');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPublishing(false);
    }
  };
  
  return (
    <div className="bg-gradient-to-r from-[#2B3A67] to-[#3d4f87] rounded-2xl p-6 text-white" data-testid="admin-actions-panel">
      <h3 className="text-lg font-extrabold mb-4 flex items-center gap-2">
        <i className="ph-bold ph-gear-six"></i>
        Admin Actions
      </h3>
      
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleCalculateSummary}
          disabled={calculating}
          className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          {calculating ? (
            <><i className="ph-bold ph-spinner animate-spin"></i> Calculating...</>
          ) : (
            <><i className="ph-bold ph-calculator"></i> Recalculate Summary</>
          )}
        </button>
        
        {!config?.is_published && (
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="bg-[#E54562] hover:bg-[#d13a55] px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
            data-testid="publish-results-btn"
          >
            {publishing ? (
              <><i className="ph-bold ph-spinner animate-spin"></i> Publishing...</>
            ) : (
              <><i className="ph-bold ph-megaphone-simple"></i> Publish Results</>
            )}
          </button>
        )}
        
        {config?.is_published && (
          <span className="bg-green-500/20 text-green-300 px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2">
            <i className="ph-bold ph-check-circle"></i> Results Published
          </span>
        )}
      </div>
    </div>
  );
}

// ----- MAIN COMPONENT -----

export default function ResultsPhase({ phase, event }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [awards, setAwards] = useState([]);
  // Admin tools only for this event's admins (owner, co-admins, Super Admins)
  const isAdmin = !!event?.can_manage;
  
  const eventId = event?.id;
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    if (eventId) {
      fetchAllData();
    }
  }, [eventId]);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [configRes, summaryRes, awardsRes] = await Promise.all([
        fetch(`${API_URL}/api/v1/events/${eventId}/results/config`),
        fetch(`${API_URL}/api/v1/events/${eventId}/results/summary`),
        fetch(`${API_URL}/api/v1/events/${eventId}/results/awards`),
      ]);
      
      if (configRes.ok) setConfig(await configRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (awardsRes.ok) setAwards(await awardsRes.json());
    } catch (err) {
      console.error('Failed to fetch results data', err);
      toast.error('Failed to load results data');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div className="space-y-6" data-testid="results-phase">
      {/* Admin Actions */}
      {isAdmin && (
        <AdminActionsPanel eventId={eventId} config={config} onRefresh={fetchAllData} />
      )}
      
      {/* Champion Banner */}
      <ChampionBanner summary={summary} />
      
      {/* Tournament Statistics */}
      <TournamentStats summary={summary} />
      
      {/* Awards Panel */}
      <AwardsPanel 
        awards={awards} 
        eventId={eventId} 
        isAdmin={isAdmin} 
        onRefresh={fetchAllData} 
      />
      
      {/* Leaderboards */}
      <LeaderboardTabs eventId={eventId} />
      
      {/* Player Statistics (Career Stats & Detailed Leaderboards) */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-extrabold mb-4 flex items-center gap-2">
          <i className="ph-bold ph-user-list text-[#2B3A67]"></i>
          Player Statistics
        </h2>
        <PlayerStatsPanel eventId={eventId} />
      </div>
      
      {/* Certificates */}
      <CertificatesPanel eventId={eventId} isAdmin={isAdmin} />
      
      {/* Social Sharing */}
      <SharingPanel eventId={eventId} />
      
      {/* Phase Rules */}
      {(phase?.rules || phase?.terms) && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          {phase.rules && (
            <div className="mb-4">
              <h3 className="font-bold mb-2 flex items-center gap-2">
                <i className="ph-bold ph-scroll text-[#2B3A67]"></i>
                Scoring Rules
              </h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{phase.rules}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
